import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { 
  Users, 
  Shield, 
  Settings, 
  Plus, 
  Trash2, 
  Search, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Crown
} from 'lucide-react';
import aldoImage from '../assets/logos/aldo-new.jpg';

interface Agent {
  id: string;
  name: string;
  description: string;
  disabled: boolean;
  adminCount: number;
  admins: AdminAssignment[];
}

interface AdminAssignment {
  email: string;
  role: 'super_admin' | 'agent_admin';
  assignedBy: string;
  assignedAt: string;
}

interface AccessListEntry {
  userEmail: string;
  accessLevel: 'allowed' | 'denied';
  addedBy: string;
  addedAt: string;
  notes: string;
}

interface UserPermissions {
  userEmail: string;
  isSuperAdmin: boolean;
  permissions: Array<{
    agentId: string;
    role: string;
    assignedAt: string;
  }>;
}

const AdminPanel: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  
  // Helper function to extract email from user claims
  const getUserEmail = (user: any): string => {
    if (!user) return '';
    
    // Check if user has email property directly
    if (user.email) return user.email;
    
    // Extract from claims array
    if (user.claims && Array.isArray(user.claims)) {
      const emailClaim = user.claims.find((claim: any) => 
        claim.typ === 'email' || 
        claim.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress' ||
        claim.typ === 'preferred_username'
      );
      if (emailClaim) return emailClaim.val;
    }
    
    // Fallback to userDetails if it looks like an email
    if (user.userDetails && user.userDetails.includes('@')) {
      return user.userDetails;
    }
    
    return user.userId || 'Unknown User';
  };
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [accessList, setAccessList] = useState<AccessListEntry[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form states
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminRole, setNewAdminRole] = useState<'agent_admin' | 'super_admin'>('agent_admin');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserAccess, setNewUserAccess] = useState<'allowed' | 'denied'>('allowed');
  const [newUserNotes, setNewUserNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      loadUserPermissions();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (userPermissions?.isSuperAdmin) {
      loadAgents();
    }
  }, [userPermissions]);

  useEffect(() => {
    if (selectedAgent) {
      loadAccessList(selectedAgent);
    }
  }, [selectedAgent]);

  const loadUserPermissions = async () => {
    try {
      const response = await fetch('/api/admin/my-permissions', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data);
      } else {
        setError('Failed to load user permissions');
      }
    } catch (err) {
      setError('Error loading permissions');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await fetch('/api/admin/agents', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded agents:', data.agents); // Debug log
        setAgents(data.agents);
      } else {
        setError('Failed to load agents');
      }
    } catch (err) {
      console.error('Error loading agents:', err); // Debug log
      setError('Error loading agents');
    }
  };

  const loadAccessList = async (agentId: string) => {
    try {
      const response = await fetch(`/api/admin/agents/${agentId}/access-list?search=${searchTerm}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccessList(data.accessList);
      } else {
        setError('Failed to load access list');
      }
    } catch (err) {
      setError('Error loading access list');
    }
  };

  const addAdmin = async (agentId: string) => {
    try {
      const response = await fetch(`/api/admin/agents/${agentId}/admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: newAdminEmail,
          role: newAdminRole
        })
      });

      if (response.ok) {
        setNewAdminEmail('');
        setShowAddAdmin(false);
        loadAgents(); // Refresh agents list
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add admin');
      }
    } catch (err) {
      setError('Error adding admin');
    }
  };

  const removeAdmin = async (agentId: string, userEmail: string) => {
    if (!window.confirm(`Remove ${userEmail} as admin for ${agentId}?`)) return;

    try {
      const response = await fetch(`/api/admin/agents/${agentId}/admins/${encodeURIComponent(userEmail)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        loadAgents(); // Refresh agents list
      } else {
        setError('Failed to remove admin');
      }
    } catch (err) {
      setError('Error removing admin');
    }
  };

  const addUserToAccessList = async () => {
    if (!selectedAgent) return;

    try {
      const response = await fetch(`/api/admin/agents/${selectedAgent}/access-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: newUserEmail,
          accessLevel: newUserAccess,
          notes: newUserNotes
        })
      });

      if (response.ok) {
        setNewUserEmail('');
        setNewUserNotes('');
        setShowAddUser(false);
        loadAccessList(selectedAgent); // Refresh access list
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add user');
      }
    } catch (err) {
      setError('Error adding user');
    }
  };

  const removeUserFromAccessList = async (userEmail: string) => {
    if (!selectedAgent || !window.confirm(`Remove ${userEmail} from access list?`)) return;

    try {
      const response = await fetch(`/api/admin/agents/${selectedAgent}/access-list/${encodeURIComponent(userEmail)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        loadAccessList(selectedAgent); // Refresh access list
      } else {
        setError('Failed to remove user');
      }
    } catch (err) {
      setError('Error removing user');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h2 className="mt-2 text-lg font-medium text-gray-900">Authentication Required</h2>
          <p className="mt-1 text-sm text-gray-500">Please sign in to access the admin panel.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!userPermissions?.isSuperAdmin && userPermissions?.permissions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h2 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h2>
          <p className="mt-1 text-sm text-gray-500">You don't have admin permissions for any agents.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-sm text-gray-500">
                  {userPermissions?.isSuperAdmin ? 'Super Administrator' : 'Agent Administrator'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {userPermissions?.isSuperAdmin && (
                <Crown className="h-5 w-5 text-yellow-500" />
              )}
              <span className="text-sm text-gray-700">{getUserEmail(user)}</span>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Agents List */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Agents
                </h3>
                <div className="space-y-3">
                  {agents.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      No agents available
                    </div>
                  )}
                  {agents.map((agent) => (
                    <div
                      key={agent.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedAgent === agent.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedAgent(agent.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={aldoImage} 
                            alt={agent.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <h4 className="font-medium text-gray-900">{agent.name}</h4>
                            <p className="text-sm text-gray-500 mt-1">{agent.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {agent.adminCount} admins
                          </span>
                          {agent.disabled && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Disabled
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Admins list */}
                      {agent.admins.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="space-y-2">
                            {agent.admins.map((admin) => (
                              <div key={admin.email} className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm text-gray-700">{admin.email}</span>
                                  {admin.role === 'super_admin' && (
                                    <Crown className="h-4 w-4 text-yellow-500" />
                                  )}
                                </div>
                                {userPermissions?.isSuperAdmin && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeAdmin(agent.id, admin.email);
                                    }}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add admin button */}
                      {userPermissions?.isSuperAdmin && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAgent(agent.id);
                              setShowAddAdmin(true);
                            }}
                            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Admin
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Access List */}
          <div className="lg:col-span-2">
            {selectedAgent ? (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Access List - {agents.find(a => a.id === selectedAgent)?.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Manage who can access this agent by adding users to the access list
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAddUser(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add User by Email
                    </button>
                  </div>

                  {/* Search */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && loadAccessList(selectedAgent)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Access list table */}
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Access
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Added By
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {accessList.map((entry) => (
                          <tr key={entry.userEmail}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {entry.userEmail}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {entry.accessLevel === 'allowed' ? (
                                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                                ) : (
                                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                                )}
                                <span className={`text-sm ${
                                  entry.accessLevel === 'allowed' ? 'text-green-800' : 'text-red-800'
                                }`}>
                                  {entry.accessLevel === 'allowed' ? 'Allowed' : 'Denied'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {entry.addedBy}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(entry.addedAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => removeUserFromAccessList(entry.userEmail)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {accessList.length === 0 && (
                      <div className="text-center py-8">
                        <Users className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No users in access list</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          This agent has no explicit access controls. All users can access it.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6 text-center">
                  <Settings className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Select an Agent</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose an agent from the list to manage its access controls.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Admin Modal */}
      {showAddAdmin && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Admin</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={newAdminRole}
                    onChange={(e) => setNewAdminRole(e.target.value as 'agent_admin' | 'super_admin')}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="agent_admin">Agent Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddAdmin(false);
                    setNewAdminEmail('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => selectedAgent && addAdmin(selectedAgent)}
                  disabled={!newAdminEmail}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  Add Admin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add User to Access List</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Access Level</label>
                  <select
                    value={newUserAccess}
                    onChange={(e) => setNewUserAccess(e.target.value as 'allowed' | 'denied')}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="allowed">Allowed</option>
                    <option value="denied">Denied</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
                  <textarea
                    value={newUserNotes}
                    onChange={(e) => setNewUserNotes(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Reason for access level..."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddUser(false);
                    setNewUserEmail('');
                    setNewUserNotes('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={addUserToAccessList}
                  disabled={!newUserEmail}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  Add User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
